// Toast stacks, the pause card and the hover tooltips of a single-player
// match. Extracted from App.tsx (ROADMAP 7.14); renders in the same DOM position.

import type { SceneNode } from '@aod/protocol';
import { PauseCard } from '../pause/PauseCard.js';
import type { PauseCard as PauseCardData } from '../pause/usePauseController.js';
import type { Vec3 } from '../render/camera/flight.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';
import { AttackToastStack } from '../ui/AttackToastStack.js';
import { DiplomacyToasts } from '../ui/DiplomacyToasts.js';
import { MerchantOfferToast } from '../ui/MerchantOfferToast.js';
import { ResearchToastStack } from '../ui/ResearchToastStack.js';
import { TileTooltip } from '../ui/TileTooltip.js';
import { UnitTooltip } from '../ui/UnitTooltip.js';
import type { useSimFeeds } from './useSimFeeds.js';

interface MatchToastLayerProps {
  sim: SimWorld;
  localPlayerIndex: number;
  feeds: ReturnType<typeof useSimFeeds>;
  pauseCard: PauseCardData | null;
  onResumeFromCard: () => void;
  onDismissCard: () => void;
  onGoTo: (position: Vec3) => void;
  hoveredTile: SceneNode | null;
  hoveredUnit: SceneNode | null;
}

export function MatchToastLayer({
  sim,
  localPlayerIndex,
  feeds,
  pauseCard,
  onResumeFromCard,
  onDismissCard,
  onGoTo,
  hoveredTile,
  hoveredUnit,
}: MatchToastLayerProps) {
  const {
    attackToasts,
    dismissToast,
    researchToasts,
    dismissResearchToast,
    pendingOffer,
    treatyDesk,
  } = feeds;
  return (
    <>
      <AttackToastStack toasts={attackToasts} onDismiss={dismissToast} />
      <ResearchToastStack toasts={researchToasts} onDismiss={dismissResearchToast} />
      <MerchantOfferToast
        cityId={pendingOffer?.cityId ?? ''}
        offer={pendingOffer?.offer ?? null}
        onAccept={sim.acceptMerchant}
        onDecline={sim.declineMerchant}
        matchOver={sim.victory !== null}
      />
      <DiplomacyToasts
        snapshot={sim.snapshot}
        players={sim.playerHudEntries}
        localPlayerIndex={localPlayerIndex}
        desk={treatyDesk}
        dynasty={sim.dynasty}
        matchOver={sim.victory !== null}
      />
      {pauseCard && (
        <PauseCard
          card={pauseCard}
          onResume={onResumeFromCard}
          dominant={sim.localPlayer?.dominant ?? null}
          onGoTo={(position) => {
            // Keep the game paused so the player can look before resuming.
            onGoTo(position);
            onDismissCard();
          }}
        />
      )}
      <TileTooltip
        tile={hoveredTile}
        units={(sim.snapshot?.scene.children ?? []).filter((n) => n.type === 'unit')}
      />
      <UnitTooltip unit={hoveredUnit} />
    </>
  );
}
